import { MarketImage } from '@/components/ui/market-image'
import { PlatformBadge } from '@/components/ui/platform-badge'
import { UntradableIndicator } from '@/components/ui/untradable-indicator'
import { formatVolume } from '@/lib/table-utils'
import { cn } from '@/lib/utils'
import type { EventListItem } from '@/types/market'
import dayjs from 'dayjs'
import { Bookmark, ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import EventCategoryBadge from '../shared/EventCategoryBadge'
import { ExpandedMarketsTable } from './expanded-markets-table'

type EventRowProps = {
	event: EventListItem
	isExpanded: boolean
	isFavorited: boolean
	onToggleFavorite: (event: EventListItem) => void
	onToggleExpand: (event: EventListItem) => void
	onSelectEvent?: (event: EventListItem) => void
}

export function EventRow({
	event,
	isExpanded,
	isFavorited,
	onToggleFavorite,
	onToggleExpand,
	onSelectEvent,
}: EventRowProps) {
	const t = useTranslations('explore')
	const eventKey = `${event.platform}:${event.id}`
	const hasExpandableMarkets = event.marketCount >= 1 && event.markets && event.markets.length > 0

	const RowWrapper = onSelectEvent
		? ({
				children,
				className,
			}: {
				children: React.ReactNode
				className?: string
			}) => (
				<button
					type="button"
					onClick={() => onSelectEvent(event)}
					className={cn(className, 'w-full text-left')}
				>
					{children}
				</button>
			)
		: ({
				children,
				className,
			}: {
				children: React.ReactNode
				className?: string
			}) => <div className={className}>{children}</div>

	const handleRowClick = () => {
		onToggleExpand(event)
	}

	return (
		<>
			<tr
				onClick={handleRowClick}
				key={eventKey}
				className={cn(
					'border-b border-b-[#282A2C] bg-brand-dark-light',
					event.isInactive && 'opacity-50',
					isExpanded && 'bg-[#282A2C]/40'
				)}
			>
				<td className="p-[16px]">
					<RowWrapper className="flex items-center gap-[12px]">
						<div className="flex items-center gap-[16px]">
							<div
								onClick={e => {
									e.preventDefault()
									e.stopPropagation()
									onToggleFavorite(event)
								}}
								className="flex size-[40px] items-center justify-center rounded-[16px] border border-brand-gray"
							>
								<Bookmark
									className="size-[20px] text-brand-gray-secondary"
									fill={isFavorited ? '#ffffffbf' : ''}
								/>
							</div>
							<div className="relative">
								<MarketImage
									src={event.imageUrl}
									alt=""
									title={event.title}
									className="size-[40px]! rounded-[16px]! font-grotesk"
								/>
								<PlatformBadge
									platform={event.platform}
									className="-right-[5px] absolute top-[26px] size-[16px]!"
								/>
							</div>
						</div>
						<div className="min-w-0 flex-1">
							<div className="mb-0.5 flex items-start gap-1.5">
								<div>
									<div className="mb-[8px] flex items-center gap-[7px]">
										<EventCategoryBadge category={event.category} />
										{event.hasUntradable && <UntradableIndicator />}
									</div>
									<p className="line-clamp-1 font-grotesk font-medium text-[16px] leading-[100%]">
										{event.title}
									</p>
								</div>
							</div>
						</div>
					</RowWrapper>
				</td>
				<td className="px-3 py-2 text-left">
					<RowWrapper>
						<span
							className={cn(
								'pl-[4px] font-grotesk font-normal text-[14px] text-white leading-[100%]'
							)}
						>
							{formatVolume(event.volume)}
						</span>
					</RowWrapper>
				</td>
				<td className="px-3 py-2 text-left">
					<RowWrapper>
						<span
							className={cn(
								'pl-[4px] text-left font-grotesk font-normal text-[14px] text-white leading-[100%]'
							)}
						>
							{formatVolume(event.liquidity)}
						</span>
					</RowWrapper>
				</td>
				<td className="px-3 py-2">
					<span className="whitespace-nowrap pl-[4px] font-grotesk font-normal text-[14px] text-white leading-[100%]">
						{event?.endDate ? (
							<>{dayjs.unix((event?.endDate * 1000) / 1000).format('MMM D, YYYY h:mm A')}</>
						) : null}
					</span>
				</td>
				<td className="px-3 py-2">
					<div className="flex items-center justify-between gap-[16px]">
						<RowWrapper>
							<span
								className={cn(
									'whitespace-nowrap pl-[4px] font-grotesk font-normal text-[14px] text-white leading-[100%]'
								)}
							>
								{event.markets?.length === 1
									? t('market', { count: 1 })
									: t('marketsCount', { count: event.markets?.length || 0 })}
							</span>
						</RowWrapper>
						{hasExpandableMarkets ? (
							<button
								type="button"
								onClick={e => {
									e.stopPropagation()
									onToggleExpand(event)
								}}
								className={cn(
									'flex size-[40px] cursor-pointer items-center justify-center rounded-[16px] border border-brand-gray',
									isExpanded && 'bg-brand-dark'
								)}
							>
								{isExpanded ? (
									<ChevronUp className="size-[20px] text-brand-gray-secondary" />
								) : (
									<ChevronDown className="size-[20px] text-brand-gray-secondary" />
								)}
							</button>
						) : null}
					</div>
				</td>
			</tr>

			{/* Expanded markets section */}
			{isExpanded && hasExpandableMarkets && (
				<ExpandedMarketsTable event={event} eventKey={eventKey} />
			)}
		</>
	)
}
